/**
 * CreateTokenModal — W2.4
 *
 * Zweistufiger Modal-Flow:
 * Schritt 1: Name-Eingabe
 * Schritt 2: Token-Einmal-Anzeige mit Bestätigungs-Checkbox
 *
 * Mobile-First: unter 375px vollbildig.
 */
import { useState, useRef, useEffect } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TokenRevealBox } from './TokenRevealBox'

type Step1Props = {
  onSubmit: (name: string) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}

function Step1Form({ onSubmit, onCancel, isLoading }: Step1Props) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('developer')

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('createTokenModal.nameRequired'))
      return
    }
    if (trimmed.length > 255) {
      setError(t('createTokenModal.nameMaxLength'))
      return
    }
    setError(null)
    await onSubmit(trimmed)
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }} noValidate>
      <div style={{ marginBottom: 20 }}>
        <label
          htmlFor="token-name-input"
          style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}
        >
          {t('createTokenModal.nameLabel')}
        </label>
        <input
          id="token-name-input"
          ref={inputRef}
          className="input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={t('createTokenModal.namePlaceholder')}
          maxLength={255}
          required
          aria-required="true"
          aria-invalid={!!error}
          aria-describedby={error ? 'token-name-error' : undefined}
          data-testid="token-name-input"
          style={{ width: '100%', boxSizing: 'border-box' }}
          disabled={isLoading}
        />
        {error && (
          <div
            id="token-name-error"
            role="alert"
            style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}
            data-testid="token-name-error"
          >
            {error}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={isLoading}
          data-testid="modal-cancel-btn"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
          aria-busy={isLoading}
          data-testid="modal-submit-btn"
        >
          {isLoading ? t('createTokenModal.submitCreating') : t('createTokenModal.submitCreate')}
        </button>
      </div>
    </form>
  )
}

type Step2Props = {
  token: string
  onClose: () => void
}

function Step2TokenReveal({ token, onClose }: Step2Props) {
  const [confirmed, setConfirmed] = useState(false)
  const { t } = useTranslation('developer')

  return (
    <div>
      <TokenRevealBox token={token} />

      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <label
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14 }}
          data-testid="confirm-copy-label"
        >
          <input
            type="checkbox"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            data-testid="confirm-copy-checkbox"
            style={{ marginTop: 2, flexShrink: 0, minWidth: 18, minHeight: 18 }}
          />
          <span>{t('createTokenModal.confirmCopy')}</span>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-primary"
          onClick={onClose}
          disabled={!confirmed}
          data-testid="modal-close-btn"
          aria-disabled={!confirmed}
        >
          {t('common:actions.close')}
        </button>
      </div>
    </div>
  )
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onCreateToken: (name: string) => Promise<string>
}

// Inner component mounts fresh on each open — state resets automatically on unmount/remount
function CreateTokenModalContent({ onClose, onCreateToken }: Omit<Props, 'isOpen'>) {
  const [step, setStep] = useState<'form' | 'reveal'>('form')
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation('developer')

  async function handleCreate(name: string) {
    setIsLoading(true)
    try {
      const token = await onCreateToken(name)
      setCreatedToken(token)
      setStep('reveal')
    } finally {
      setIsLoading(false)
    }
  }

  function handleClose() {
    onClose()
  }

  function handleOverlayClick(e: React.MouseEvent) {
    // Nur schließen wenn auf Overlay geklickt (nicht im Step 2 — Nutzer muss Checkbox aktivieren)
    if (e.target === e.currentTarget && step === 'form') {
      handleClose()
    }
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={step === 'form' ? t('createTokenModal.modalAriaLabel') : t('createTokenModal.revealAriaLabel')}
      data-testid="create-token-modal"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="card modal-card"
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '24px',
          width: '100%',
          maxWidth: 480,
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          // Mobile < 375px: vollbildig
          boxSizing: 'border-box',
        }}
        data-testid="modal-card"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 12 }}>
          <h2 style={{ margin: 0, flex: 1, fontSize: 18, fontWeight: 600 }}>
            {step === 'form' ? t('createTokenModal.titleForm') : t('createTokenModal.titleReveal')}
          </h2>
          {step === 'form' && (
            <button
              className="btn btn-icon btn-ghost"
              onClick={handleClose}
              aria-label={t('createTokenModal.closeBtn')}
              data-testid="modal-x-btn"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Step-Content */}
        {step === 'form' && (
          <Step1Form
            onSubmit={handleCreate}
            onCancel={handleClose}
            isLoading={isLoading}
          />
        )}

        {step === 'reveal' && createdToken && (
          <Step2TokenReveal
            token={createdToken}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  )
}

export function CreateTokenModal({ isOpen, onClose, onCreateToken }: Props) {
  if (!isOpen) return null
  return (
    <CreateTokenModalContent
      onClose={onClose}
      onCreateToken={onCreateToken}
    />
  )
}
